import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    SelectChangeEvent
} from '@mui/material';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { request } from 'app/network';

interface AddTenantModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
}

const AddTenantModal: React.FC<AddTenantModalProps> = ({ open, onClose, onSuccess, darkMode }) => {
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        tenantType: 'CORP',
        maxUsers: '',
        isActive: true,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (e: SelectChangeEvent) => {
        setFormData(prev => ({ ...prev, tenantType: e.target.value }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, isActive: e.target.checked }));
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.id || !formData.name) {
            setError('아이디와 이름은 필수입니다.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const payload = {
                Id: formData.id,
                Name: formData.name,
                Description: formData.description,
                TenantType: formData.tenantType,
                MaxUsers: formData.maxUsers ? parseInt(formData.maxUsers, 10) : null,
                IsActive: formData.isActive
            };

            await request('Tenant.create', {
                paramz: payload
            });

            setLoading(false);
            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('Create Tenant Error:', err);
            setError(err.message || '테넌트 생성 중 오류가 발생했습니다.');
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            id: '',
            name: '',
            description: '',
            tenantType: 'CORP', // Reset to default
            maxUsers: '',
            isActive: true,
        });
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? '#1a2035' : '#fff',
                    backgroundImage: 'none'
                }
            }}
        >
            <DialogTitle sx={{ color: darkMode ? '#fff' : '#000' }}>테넌트 추가</DialogTitle>
            <DialogContent>
                <MDBox component="form" role="form" mt={2}>
                    <MDBox mb={2}>
                        <MDInput
                            type="text"
                            label="Tenant ID (Required)"
                            name="id"
                            value={formData.id}
                            onChange={handleChange}
                            fullWidth
                            error={!!error && !formData.id}
                        />
                    </MDBox>
                    <MDBox mb={2}>
                        <MDInput
                            type="text"
                            label="Tenant Name (Required)"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            fullWidth
                            error={!!error && !formData.name}
                        />
                    </MDBox>
                    <MDBox mb={2}>
                        <FormControl fullWidth>
                            <InputLabel id="tenant-type-label" sx={{ color: darkMode ? '#fff' : 'rgba(0, 0, 0, 0.6)' }}>Type</InputLabel>
                            <Select
                                labelId="tenant-type-label"
                                value={formData.tenantType}
                                label="Type"
                                onChange={handleSelectChange}
                                sx={{ height: '44px' }}
                            >
                                <MenuItem value="CORP">기업 (CORP)</MenuItem>
                                <MenuItem value="HOSP">병원 (HOSP)</MenuItem>
                                <MenuItem value="CLINIC">의원 (CLINIC)</MenuItem>
                                <MenuItem value="GYM">체육관 (GYM)</MenuItem>
                            </Select>
                        </FormControl>
                    </MDBox>
                    <MDBox mb={2}>
                        <MDInput
                            type="text"
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={3}
                        />
                    </MDBox>
                    <MDBox mb={2}>
                        <MDInput
                            type="number"
                            label="Max Users"
                            name="maxUsers"
                            value={formData.maxUsers}
                            onChange={handleChange}
                            fullWidth
                            sx={{
                                "& input[type=number]": {
                                    colorScheme: darkMode ? "dark" : "normal",
                                },
                            }}
                        />
                    </MDBox>
                    <MDBox mb={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isActive}
                                    onChange={handleCheckboxChange}
                                    color="primary"
                                />
                            }
                            label="Is Active"
                            sx={{ color: 'text.primary' }}
                        />
                    </MDBox>
                    {error && (
                        <MDBox mb={2}>
                            <p style={{ color: 'red', fontSize: '0.875rem' }}>{error}</p>
                        </MDBox>
                    )}
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={handleClose} color="secondary">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSubmit}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? '생성 중...' : '생성'}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default AddTenantModal;
